import { useEffect, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";

import { authTokenAtom } from "atoms/authAtoms";
import { errorAtom } from "atoms/error";
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { toUserMessage } from 'utils/apiErrorMessage';
import { copyToConfigAtom } from "atoms/Rtool/copyToConfigAtoms";
import { mappingAtom } from "atoms/mappingAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { PHENOMENON_IDS } from "utils/consts";
import { PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";
import { submitDimensions, submitDimensionsHev } from "api/submitRtool";
import { fetchDimensions, fetchDimensionsHev } from "api/fetchRtool";
import { isConfigComplete } from "utils/utils";
import { getMappingLabel } from "utils/mappingLabel";
import { buildDimensionsParams } from "utils/dimensionsForm";
import { useIsCopyJobBlocking } from "utils/hooks/useCopyJobBlocking";

export const ManualInput: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(copyToConfigAtom);
    const mapping = useAtomValue(mappingAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const setError = useSetAtom(errorAtom);
    const { showSuccess, showError } = useSnackbar();
    const isCopyBlocking = useIsCopyJobBlocking(powertrain, config);

    const isHev = powertrain === 'hev';
    // HEV は aco を含まない HEV 固有の phenomenon 値域。BEV も同じ flat 形式に統一済み（諸元データ仕様変更 #26）
    const phenomenonIds: readonly string[] = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;

    const [loading, setLoading] = useState(false);

    // 共通: tire_radius（calc 共通スカラー）
    const [tire_radius, setTireRadius] = useState<string>("");

    // gear_ratio / motor_order は phenomenon_id ごとの flat Dict（BEV/HEV 共通、運転モード非依存）
    const [gearRatios, setGearRatios] = useState<Record<string, string>>({});
    const [motorOrders, setMotorOrders] = useState<Record<string, string>>({});

    const { project_id, destination_id, lot_id, phase_id, unit_id, calculation_id } = config;
    // HEV は gear 選択も前提
    const ready = isConfigComplete(config) && (!isHev || Boolean(gearId));

    // 登録済み諸元データをデフォルトとして反映（諸元データ仕様変更 #27）
    useEffect(() => {
        const reset = () => {
            setTireRadius("");
            setGearRatios(Object.fromEntries(phenomenonIds.map((p) => [p, ""])));
            setMotorOrders(Object.fromEntries(phenomenonIds.map((p) => [p, ""])));
        };

        if (!ready) {
            reset();
            return;
        }

        let cancelled = false;
        const configForFetch = { project_id, destination_id, lot_id, phase_id, unit_id, calculation_id };

        const fetchDefaults = async () => {
            try {
                const data = isHev
                    ? await fetchDimensionsHev(authToken, configForFetch, gearId!)
                    : await fetchDimensions(authToken, configForFetch);
                if (cancelled) return;
                setTireRadius(Number.isFinite(data.tire_radius) ? String(data.tire_radius) : "");
                const nextGearRatios = Object.fromEntries(phenomenonIds.map((p) => [p, ""])) as Record<string, string>;
                Object.entries(data.gear_ratio || {}).forEach(([phenomenonId, value]) => {
                    if (phenomenonId in nextGearRatios && Number.isFinite(value)) {
                        nextGearRatios[phenomenonId] = String(value);
                    }
                });
                setGearRatios(nextGearRatios);
                const nextMotorOrders = Object.fromEntries(phenomenonIds.map((p) => [p, ""])) as Record<string, string>;
                Object.entries(data.motor_order || {}).forEach(([phenomenonId, value]) => {
                    if (phenomenonId in nextMotorOrders && Number.isFinite(value)) {
                        nextMotorOrders[phenomenonId] = String(value);
                    }
                });
                setMotorOrders(nextMotorOrders);
            } catch (e) {
                if (cancelled) return;
                reset();
                console.error("諸元データの取得に失敗しました:", e);
                showError("登録済み諸元データの取得に失敗しました。入力欄は空のままです。");
            }
        };

        fetchDefaults();

        return () => {
            cancelled = true;
        };
    }, [
        authToken,
        gearId,
        isHev,
        phenomenonIds,
        project_id,
        destination_id,
        lot_id,
        phase_id,
        unit_id,
        calculation_id,
        ready,
        showError,
    ]);

    const dimensionsParams = buildDimensionsParams(tire_radius, gearRatios, motorOrders, phenomenonIds);
    const paramsValid = dimensionsParams !== null;

    const handleSubmit = async () => {
        if (isHev && !gearId) return;
        if (!dimensionsParams) return;
        setLoading(true);
        setError(null);
        try {
            const response = isHev
                ? await submitDimensionsHev(authToken, config, gearId!, dimensionsParams)
                : await submitDimensions(authToken, config, dimensionsParams);
            showSuccess(response);
        } catch (e) {
            setError(toUserMessage(e));
            showError(toUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={2}>
            <Typography>UIから直接入力します。</Typography>

            {/* tire_radius（BEV / HEV 共通、calc 共通スカラー） */}
            <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>タイヤ半径</Typography>
                <TextField
                    label="tire_radius (float > 0)"
                    type="number"
                    fullWidth
                    margin="dense"
                    value={tire_radius}
                    onChange={(e) => setTireRadius(e.target.value)}
                />
            </Box>

            {isHev && !gearId && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    HEV では先に Gear を選択してください。
                </Typography>
            )}

            <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
                現象IDごとに gear_ratio と motor_order を入力します（運転モード非依存）。
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
                {phenomenonIds.map((p) => (
                    <Box key={p} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {getMappingLabel(mapping, 'phenomenon_id', p)}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                label="gear_ratio (float > 0)"
                                type="number"
                                fullWidth
                                value={gearRatios[p] ?? ""}
                                onChange={(e) =>
                                    setGearRatios((prev) => ({ ...prev, [p]: e.target.value }))
                                }
                            />
                            <TextField
                                label="motor_order (float > 0)"
                                type="number"
                                fullWidth
                                value={motorOrders[p] ?? ""}
                                onChange={(e) =>
                                    setMotorOrders((prev) => ({ ...prev, [p]: e.target.value }))
                                }
                            />
                        </Stack>
                    </Box>
                ))}
            </Stack>

            <Button
                variant="contained"
                sx={{ mt: 2, minWidth: 140 }}
                disabled={!isConfigComplete(config) || !paramsValid || loading || (isHev && !gearId) || isCopyBlocking}
                onClick={handleSubmit}
                startIcon={loading ? <CircularProgress size={18} /> : undefined}
            >
                {loading ? "登録中..." : "諸元データ登録"}
            </Button>

            {isCopyBlocking && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    コピー実行中のため操作できません。
                </Typography>
            )}
        </Box>
    );
};
